/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
/**
 * Adding the eslint disable rule above, as we will be updating the nested css in this file in phases
 */

import { PropsWithChildren, ReactElement } from "react";

import { css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useCurrentNav, DivOnHoverExit } from "./nav-links-container";

type TeamData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon: {
        normal: ReactElement;
        hover?: ReactElement;
    };
};

const icons = {
    software: {
        normal: (
            <img
                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg1.svg"
                width="36"
                height="36"
                alt="software-icon"
                loading="lazy"
            />
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({ position: "absolute", insetBlockStart: "-8px" })}
                >
                    <img
                        src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg2.svg"
                        width="38"
                        height="36"
                        alt="software-icon-hover"
                        loading="lazy"
                    />
                </div>
            </div>
        )
    },
    marketing: {
        normal: (
            <img
                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg5.svg"
                width="36"
                height="36"
                alt="marketing-icon"
                loading="lazy"
            />
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "0.5px"
                    })}
                >
                    <img
                        src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg2-1.svg"
                        width="44"
                        height="28"
                        alt="marketing-icon-hover"
                        loading="lazy"
                    />
                </div>
            </div>
        )
    },
    it: {
        normal: (
            <img
                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg7.svg"
                width="36"
                height="36"
                alt="it-icon"
                loading="lazy"
            />
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "-4.5px"
                    })}
                >
                    <img
                        src="https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-2632-svg8.svg"
                        width="32"
                        height="28"
                        alt="it-icon-hover"
                        loading="lazy"
                    />
                </div>
            </div>
        )
    }
} as const;

function useTeamsData(): Array<TeamData> {
    return [
        {
            id: "teams.software",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.software.title",
                defaultMessage: "Software"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.software.content",
                defaultMessage: "Ship high-quality, innovative software faster"
            }),
            href: useLocalizedHref("/teams/software-development"),
            icon: icons.software
        },
        {
            id: "teams.marketing",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.title",
                defaultMessage: "Marketing"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.content",
                defaultMessage: "Collaborative strategies for marketing success"
            }),
            href: useLocalizedHref("/teams/marketing"),
            icon: icons.marketing
        },
        // {
        //     id: "teams.project-management",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.projectmanagement.title",
        //         defaultMessage: "Project management"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.projectmanagement.content",
        //         defaultMessage:
        //             "Plan, track, and optimize projects for smooth delivery"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.projectManagement
        // },
        // {
        //     id: "teams.design",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.design.title",
        //         defaultMessage: "Design"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.design.content",
        //         defaultMessage:
        //             "Align on user needs and craft breathtaking experiences"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.design
        // },
        // {
        //     id: "teams.product-management",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.productmanagement.title",
        //         defaultMessage: "Product management"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.productmanagement.content",
        //         defaultMessage:
        //             "Master the product lifecycle, from brainstorm to launch"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.productManagement
        // },
        {
            id: "teams.it",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.it.title",
                defaultMessage: "IT"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.it.content",
                defaultMessage:
                    "Deliver exceptional operations and support services."
            }),
            href: useLocalizedHref("/teams/it"),
            icon: icons.it
        }
    ];
}

function useTeamsLinks() {
    return {
        solution: {
            id: "solution",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.solution",
                defaultMessage: "Solution"
            }),
            items: [
                {
                    id: "work-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.work-management",
                        defaultMessage: "Work Management"
                    }),
                    href: useLocalizedHref("/solutions/work-management")
                },
                {
                    id: "it-service-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.it-service-management",
                        defaultMessage: "IT Service Management"
                    }),
                    href: useLocalizedHref("/solutions/itsm")
                },
                {
                    id: "agile-devops",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.agile-devops",
                        defaultMessage: "Agile & DevOps"
                    }),
                    href: useLocalizedHref("/solutions/devops")
                }
            ]
        },
        byTeamSize: {
            id: "byTeamSize",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-team-size",
                defaultMessage: "By Team Size"
            }),
            items: [
                {
                    id: "enterprise",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.enterprise",
                        defaultMessage: "Enterprise"
                    }),
                    href: useLocalizedHref("/enterprise")
                },
                {
                    id: "small-business",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.small-business",
                        defaultMessage: "Small Business"
                    }),
                    href: useLocalizedHref("/software/small-business")
                },
                {
                    id: "startup",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.startup",
                        defaultMessage: "Startup"
                    }),
                    href: useLocalizedHref("/software/startups")
                },
                {
                    id: "non-profit",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.non-profit",
                        defaultMessage: "Non-profit"
                    }),
                    href: useLocalizedHref("/teams/nonprofits")
                }
            ]
        },
        byIndustry: {
            id: "byIndustry",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-industry",
                defaultMessage: "By Industry"
            }),
            items: [
                {
                    id: "retail",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.retail",
                        defaultMessage: "Retail"
                    }),
                    href: useLocalizedHref("/industries/retail")
                },
                {
                    id: "telecommunications",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.telecommunications",
                        defaultMessage: "Telecommunications"
                    }),
                    href: useLocalizedHref("/industries/telecom")
                },
                {
                    id: "professional-services",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.professional-services",
                        defaultMessage: "Professional services"
                    }),
                    href: useLocalizedHref("/industries/professional-services")
                },
                {
                    id: "government",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.government",
                        defaultMessage: "Government"
                    }),
                    href: useLocalizedHref("/government")
                }
            ]
        }
    } as const;
}

const TeamsGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={3}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const TeamsSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const teamsData = useTeamsData();
    const teamsLinks = useTeamsLinks();
    const linkColumns = Object.values(teamsLinks);

    const { direction } = useCurrentNav();

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <TeamsGrid>
                        {teamsData.map(({ id, title, content, ...rest }) => (
                            <TitleCard
                                key={id}
                                title={intl.formatMessage(title)}
                                content={intl.formatMessage(content)}
                                productKey={productKey}
                                {...rest}
                            />
                        ))}
                    </TeamsGrid>
                    <div css={css({ backgroundColor: "#F8F8F8" })}>
                        <RowGrid
                            numCols={3}
                            columnGap={token("space.400")}
                            paddingBlock={token("space.500")}
                            paddingInline={token("space.500")}
                        >
                            {linkColumns.map((column) => {
                                return (
                                    <LinkColumn key={column.id}>
                                        <span
                                            css={css({
                                                textTransform: "uppercase",
                                                color: "#101214",
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    "var(--bxp-charlie-text-font-family)",
                                                fontWeight: 700,
                                                lineHeight: "1rem"
                                            })}
                                        >
                                            {intl.formatMessage(column.title)}
                                        </span>
                                        <div
                                            css={css({
                                                display: "grid",
                                                gridTemplateRows:
                                                    "40px 40px 40px 40px"
                                            })}
                                        >
                                            {column.items.map((link) => {
                                                return (
                                                    <div
                                                        key={link.id}
                                                        css={css({
                                                            a: {
                                                                "fontWeight": 400,
                                                                "fontSize":
                                                                    "0.875rem",
                                                                "color":
                                                                    "#101214",
                                                                "&:visited": {
                                                                    textDecoration:
                                                                        "none",
                                                                    color: "#101214"
                                                                },
                                                                "&:hover, &:focus":
                                                                    {
                                                                        color: "#0747A6"
                                                                    }
                                                            },

                                                            display: "flex",
                                                            alignItems: "end"
                                                        })}
                                                    >
                                                        <Link
                                                            type="anchor"
                                                            href={link.href}
                                                            text={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            ariaLabel={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            dataEventProps={{
                                                                "data-event":
                                                                    "clicked",
                                                                "data-event-component":
                                                                    "linkButton",
                                                                "data-event-container":
                                                                    "globalNavSubnav",
                                                                "data-product-key":
                                                                    productKey
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </LinkColumn>
                                );
                            })}
                        </RowGrid>
                    </div>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
